import { createFileRoute } from '@tanstack/react-router'
import { type Cue, CUE_NAMES } from '../lib/cue-synth'
import { previewCue, setSoundEnabled, useSoundEnabled } from '../lib/sound'

export const Route = createFileRoute('/admin/sounds')({
  component: SoundsPage,
})

/** What each cue is for, in the order somebody would meet them. */
const CUES: { name: Cue; title: string; when: string }[] = [
  { name: 'toggled', title: 'Toggled', when: 'A filter, a sort, or a switch changing what is on screen' },
  { name: 'opened', title: 'Opened', when: 'Following a game through to its page' },
  { name: 'dismissed', title: 'Dismissed', when: 'Closing a sheet, or jumping back to the top of the library' },
  { name: 'revealed', title: 'Revealed', when: 'A key being uncovered' },
  { name: 'copied', title: 'Copied', when: 'A code going to the clipboard — and confirming sound being switched on' },
  { name: 'saved', title: 'Saved', when: 'A game, key, store or source saved in admin' },
  { name: 'sent', title: 'Sent', when: 'A request submitted from the public site' },
  { name: 'failed', title: 'Failed', when: 'Anything that did not go through' },
]

/**
 * Somewhere to actually hear the interaction sounds.
 *
 * Tuning a cue by triggering it in the wild means finding a game to open or a
 * key to copy every time you want to hear one note change, and comparing two
 * of them means doing that twice. Eight buttons in a column is the whole
 * point: the set has to sound like a set, and that is only judgeable back to
 * back.
 */
function SoundsPage() {
  const enabled = useSoundEnabled()

  // A cue added to the palette and forgotten here would be untunable, so the
  // page checks rather than trusting the list above to have kept up.
  const missing = CUE_NAMES.filter((name) => !CUES.some((entry) => entry.name === name))

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Sounds</h1>
      <p className="mt-2 text-sm text-ink-soft">
        Every interaction sound the site can make. These play here whether or not sound is switched
        on, so you can listen without turning it on for normal browsing.
      </p>

      <div className="mt-4 flex items-center gap-3 rounded-xl border border-line bg-surface-1 px-4 py-3">
        <span className="text-xs text-ink-soft">
          Sound is currently <strong className="text-ink">{enabled ? 'on' : 'off'}</strong> for browsing
        </span>
        <button
          type="button"
          onClick={() => setSoundEnabled(!enabled)}
          className="ml-auto rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink"
        >
          Turn {enabled ? 'off' : 'on'}
        </button>
      </div>

      <ul className="mt-4 overflow-hidden rounded-xl border border-line">
        {CUES.map((entry, index) => (
          <li
            key={entry.name}
            className={`flex items-center gap-4 bg-surface-1 px-4 py-3 ${
              index > 0 ? 'border-t border-line' : ''
            }`}
          >
            <div className="min-w-0">
              <p className="text-sm text-ink">{entry.title}</p>
              <p className="mt-0.5 text-xs text-ink-muted">{entry.when}</p>
            </div>

            <button
              type="button"
              onClick={() => previewCue(entry.name)}
              aria-label={`Play the ${entry.title.toLowerCase()} sound`}
              className="ml-auto flex shrink-0 items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft transition-colors hover:border-accent hover:text-ink"
            >
              <svg aria-hidden viewBox="0 0 16 16" className="size-3">
                <path d="M4.5 3.2 12 8l-7.5 4.8V3.2Z" fill="currentColor" />
              </svg>
              Play
            </button>
          </li>
        ))}
      </ul>

      {missing.length > 0 && (
        <p className="mt-3 text-xs text-warn">
          Not listed above, so untunable from here: {missing.join(', ')}
        </p>
      )}

      <p className="mt-4 text-xs text-ink-muted">
        The sounds are synthesized rather than played from files — the pitches, lengths and volumes
        live in <code className="text-ink-soft">web/src/lib/cue-synth.ts</code>. Say what is wrong
        with one and it is a number to change, not a different sound to go and find.
      </p>
    </div>
  )
}
